// Cross-tab timer sync (ENG-116).
//
// Every tab runs its own copy of the timer state, persisted to localStorage so a
// refresh keeps it. Before this, a tab only READ that key on mount, so stopping the
// timer in one tab left it ticking in every other open tab -- and the next click in
// a stale tab rewrote the key, so a brand-new tab restored the timer you had stopped.
//
// Tabs now listen for the browser `storage` event (fires in every OTHER tab of the
// same origin). Two signals:
//   - TIMER_STOP_SIGNAL_KEY: written on an explicit stop/reset/auto-save -> other tabs stop.
//   - TIMER_STORAGE_KEY: a running/paused state written elsewhere -> other tabs adopt it.
// Removal of TIMER_STORAGE_KEY is deliberately NOT a stop: a freshly mounting tab
// removes it for one render before its restore effect lands, which would otherwise
// kill the timer in every other tab.

import type { HeldTimer } from "./timerHold";

export const TIMER_STORAGE_KEY = "commandhub_timer_state";
export const TIMER_STOP_SIGNAL_KEY = "commandhub_timer_stopped_at";

export interface PersistedTimerState {
  timerRunning: boolean;
  timerPaused: boolean;
  timerStart: number | null;
  accumulatedSeconds: number;
  lastActivityTime: number;
  timerStartTimestamp: string | null;
  startTime: string;
  savedAt: number;
  date: string;
  selectedAccount: string;
  selectedProject: string;
  selectedCode: string;
  notes: string;
  isBillable: boolean;
  // v1.5.90: a stopped-but-unsaved entry (the "ready to save" banner) and a timer on hold.
  timerStopped: boolean;
  endTime: string;
  duration: number;
  held: HeldTimer | null;
}

export type TimerSyncAction =
  | { type: "ignore" }
  | { type: "stop" }
  | { type: "adopt"; state: PersistedTimerState };

// Fields that change on every write without meaning anything to another tab.
// Comparing them would make two tabs re-adopt each other's echo forever.
const NOISE_FIELDS = new Set<keyof PersistedTimerState>(["savedAt", "lastActivityTime"]);

function sameState(a: PersistedTimerState, b: PersistedTimerState): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof PersistedTimerState>;
  for (const k of keys) {
    if (NOISE_FIELDS.has(k)) continue;
    // JSON compare so the nested `held` object is compared by value, not by reference.
    if (JSON.stringify(a[k] ?? null) !== JSON.stringify(b[k] ?? null)) return false;
  }
  return true;
}

/**
 * Decide what this tab should do with a `storage` event.
 * @param current this tab's timer as it would be persisted, or null when idle.
 */
export function timerSyncAction(
  key: string | null,
  newValue: string | null,
  current: PersistedTimerState | null,
  today: string,
): TimerSyncAction {
  if (key === TIMER_STOP_SIGNAL_KEY) {
    return newValue !== null && current ? { type: "stop" } : { type: "ignore" };
  }
  if (key !== TIMER_STORAGE_KEY || newValue === null) return { type: "ignore" };

  let incoming: PersistedTimerState;
  try {
    incoming = JSON.parse(newValue);
  } catch {
    return { type: "ignore" };
  }
  if (!incoming || incoming.date !== today) return { type: "ignore" };
  if (!incoming.timerRunning && !incoming.timerPaused && !incoming.timerStopped && !incoming.held) {
    return { type: "ignore" };
  }
  if (current && sameState(current, incoming)) return { type: "ignore" };
  return { type: "adopt", state: incoming };
}

/** What this tab last applied from another tab (see shouldSkipEcho). */
export type RemoteMarker = { kind: "adopt"; state: PersistedTimerState } | { kind: "stop" } | null;

/**
 * A tab must not write back a state it just received from another tab. Applying a
 * remote change happens in steps (a stop, then the new state), and writing the
 * half-applied state raced the origin tab's write: a tab holding a timer could
 * overwrite another tab's stopped, unsaved entry. The marker clears once this tab
 * changes the timer itself.
 */
export function shouldSkipEcho(marker: RemoteMarker, current: PersistedTimerState | null): boolean {
  if (!marker) return false;
  if (marker.kind === "adopt") return !!current && sameState(current, marker.state);
  return !current || (!current.timerRunning && !current.timerPaused && !current.timerStopped);
}
